import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ElementType,
  PropsWithChildren,
  ReactNode,
  RefAttributes,
  RefObject,
} from 'react';
import { twJoin } from 'tailwind-merge';

import { AlertIcon } from '@/core/tamagoshiTailwind/icons/AlertIcon';
import { CheckCircleFullIcon } from '@/core/tamagoshiTailwind/icons/CheckCircleFullIcon';
import type { TamagoshiIconComponent } from '@/core/tamagoshiTailwind/interfaces';

export type BaseInputSettings<T extends ElementType> = {
  /** This will make available a helper text in the component */
  helperText?: boolean;
  /** List of statuses that the component will accept */
  statuses?: ('default' | 'error' | 'success' | 'disabled')[];
  /** Border radius style */
  rounded?: 'card' | 'full';
  /**
   * Whether the component should treat an empty element (this means, with no
   * children) as an empty value.
   * This is only useful when you use a custom input element.
   */
  detectEmpty?: boolean;
  /** The element that will be used as base for the component */
  as: T;
  /** Whether the component requires a label  */
  requiredLabel?: boolean;
};

type BaseCustomBaseInputProps = {
  /** Reference to the root element ref */
  rootRef?: RefObject<HTMLLabelElement>;
  /**
   * Reference to the input container element ref. The element don't contain the
   * helper text.
   */
  inputContainerRef?: RefObject<HTMLDivElement>;
  startIcon?: ReactNode;
  startAddon?: ReactNode;
  endIcon?: ReactNode;
  forceHoverState?: boolean;
};

export type AllOptionalProps = {
  helperText?: string;
  status?: NonNullable<BaseInputSettings<any>['statuses']>[number];
  label?: string;
  isEmpty?: boolean;
};

type OmittedProps = 'disabled';

export type BaseInputProps<
  T extends ElementType,
  Settings extends BaseInputSettings<T>,
> = PropsWithChildren<
  BaseCustomBaseInputProps &
    Omit<
      ComponentPropsWithoutRef<T>,
      keyof BaseCustomBaseInputProps | keyof AllOptionalProps | OmittedProps
    > &
    HelperText<T, Settings['helperText']> &
    Status<T, Settings['statuses']> &
    DetectEmpty<T, Settings['detectEmpty']> &
    Label<T, Settings['requiredLabel']>
>;

export type BaseInputElementType<
  T extends ElementType,
  S extends BaseInputSettings<T>,
> = S['as'] extends undefined
  ? (props: BaseInputProps<T, S> & RefAttributes<ElementRef<T>>) => JSX.Element
  : (
      props: BaseInputProps<NonNullable<S['as']>, S> &
        RefAttributes<ElementRef<NonNullable<S['as']>>>,
    ) => JSX.Element;

type EffectiveTag<Settings extends BaseInputSettings<any>> =
  Settings['as'] extends 'textarea' ? 'textarea' : 'input';

type ExcludedProps =
  | 'checked'
  | 'defaultChecked'
  | 'style'
  | 'className'
  | 'type';

export type ExtractBaseInputPropsType<
  Settings extends BaseInputSettings<any>,
  // eslint-disable-next-line @typescript-eslint/ban-types
  Base extends Record<string, unknown> = {},
  Tag extends ElementType = EffectiveTag<Settings>,
> = Omit<ComponentPropsWithoutRef<Tag>, ExcludedProps> &
  Base &
  HelperText<Tag, Settings['helperText']> &
  Status<Tag, Settings['statuses']> &
  Label<Tag, Settings['requiredLabel']>;

type HelperText<
  T extends ElementType,
  S extends BaseInputSettings<T>['helperText'],
> = S extends true
  ? /** Helper text to display */
    { helperText?: string }
  : // eslint-disable-next-line @typescript-eslint/ban-types
    {};

type Status<
  T extends ElementType,
  S extends BaseInputSettings<T>['statuses'],
> = S extends undefined
  ? // eslint-disable-next-line @typescript-eslint/ban-types
    {}
  : {
      /** Status of the component */
      status?: NonNullable<S>[number];
    };

type DetectEmpty<
  T extends ElementType,
  S extends BaseInputSettings<T>['detectEmpty'],
> = S extends true
  ? {
      /**
       * If you have used a custom input element as base and it does not detect
       * when the input has no value, in order to have the label animation
       * working, you must specify whether the input is empty or not with this
       * property.
       */
      isEmpty?: boolean;
    }
  : // eslint-disable-next-line @typescript-eslint/ban-types
    {};

type Label<
  T extends ElementType,
  S extends BaseInputSettings<T>['requiredLabel'],
> = S extends true
  ? {
      /** Label to display */
      label: string;
    }
  : {
      /** Label to display */
      label?: string;
    };

export function mergeWithDefaults<T extends ElementType>(
  settings: BaseInputSettings<T>,
): Required<BaseInputSettings<T>> {
  const defaultSettings: Required<BaseInputSettings<T>> = {
    helperText: false,
    statuses: ['default'],
    rounded: 'card',
    detectEmpty: false,
    requiredLabel: false,
    as: 'input' as T,
  };

  return {
    ...defaultSettings,
    ...settings,
  };
}

export const STATUS_STYLES = {
  default: {
    textColor: 'text-foundation-primary',
    icon: undefined,
  },
  error: {
    textColor: 'text-error-primary',
    icon: AlertIcon,
  },
  success: {
    textColor: 'text-success-primary',
    icon: CheckCircleFullIcon,
  },
  disabled: {
    textColor: 'text-foundation-disabled',
    icon: undefined,
  },
} as const satisfies Record<
  NonNullable<BaseInputSettings<any>['statuses']>[number],
  {
    textColor: string;
    icon: TamagoshiIconComponent | undefined;
  }
>;

export const STATUS_BORDER_CLASSES = twJoin(
  `group-[:is([data-status="default"]):not([data-force-hover-state])]/container:border-foundation-secondary
  group-data-[disabled]/container:border-foundation-disabled
  group-data-[force-hover-state]/container:border-foundation-tertiary
  group-data-[status="error"]/container:border-error-primary
  group-data-[status="success"]/container:border-success-primary
  group-data-[status="default"]/container:group-hover/hover:border-foundation-tertiary`,
);

export const DISABLED_CLASSES = twJoin(
  `group-hover/container:disabled:border-foundation-disabled',
  group-data-[disabled]/container:border-foundation-disabled
  group-data-[disabled]/container:bg-foundation-disabled
  group-data-[disabled]/container:text-foundation-disabled`,
);

export const ROUNDED_VALUES = {
  card: 'rounded-r-card group-first:rounded-l-card',
  full: 'rounded-r-full group-first:rounded-l-full',
} as const satisfies Record<
  NonNullable<BaseInputSettings<any>['rounded']>,
  string
>;
